import { useState, useEffect } from 'react';
import { UserPlus, UserMinus, ChevronDown, Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { InlineConfirmation } from '../ui/InlineConfirmation';
import { apiClient } from '../../lib/api-client';
import { Skeleton } from '../ui/Skeleton';
import { DropdownMenu } from '../ui/DropdownMenu';

interface Member {
  value: string;
  display: string;
}

interface ManageMembersDialogProps {
  isOpen: boolean;
  groupId: string;
  groupName: string;
  onClose: () => void;
  onUpdate: () => void;
}

export function ManageMembersDialog({
  isOpen,
  groupId,
  groupName,
  onClose,
  onUpdate
}: ManageMembersDialogProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, groupId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [groupRes, usersRes] = await Promise.all([
        apiClient.get(`/api/admin/yaml-groups/${groupName}`),
        apiClient.get('/api/admin/users')
      ]);
      // Extract members from group
      const memberEmails = groupRes.group?.members || [];
      setMembers(memberEmails.map((email: string) => ({ value: email, display: email })));
      setAllUsers(usersRes.users || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId) return;

    setIsSubmitting(true);
    try {
      // selectedUserId is actually the user email
      await apiClient.post(`/api/admin/yaml-groups/${groupName}/members/${selectedUserId}`, {});
      setSelectedUserId('');
      await fetchData();
      onUpdate();
    } catch (error) {
      console.error('Failed to add user:', error);
      alert('Failed to add user to group');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveUser = async (userEmail: string) => {
    setIsSubmitting(true);
    try {
      await apiClient.delete(`/api/admin/yaml-groups/${groupName}/members/${userEmail}`);
      await fetchData();
      onUpdate();
    } catch (error) {
      console.error('Failed to remove user:', error);
      alert('Failed to remove user from group');
    } finally {
      setIsSubmitting(false);
      setConfirmingRemove(null);
    }
  };

  const memberIds = members.map(m => m.value);
  const availableUsers = allUsers.filter(u => !memberIds.includes(u.id));
  const selectedUserLabel = (() => {
    if (!selectedUserId) return 'Select a user...';
    const selected = availableUsers.find((u: any) => {
      const email = (u as any).email || u.emails?.[0]?.value || u.userName || '';
      return email === selectedUserId;
    });
    if (!selected) return selectedUserId;
    const email = (selected as any).email || selected.emails?.[0]?.value || selected.userName || selectedUserId;
    const name = typeof selected.name === 'string' ? selected.name : (selected.name?.formatted || '');
    return name ? `${name} (${email})` : email;
  })();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent maxWidth="2xl">
        <DialogHeader>
          <DialogTitle>Manage Group Members</DialogTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{groupName}</p>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
          <div className="space-y-4 py-4">
            <Skeleton className="h-10 w-full" />
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Add User to Group
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Desktop native select */}
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="hidden md:block flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
                  disabled={isSubmitting}
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map(user => {
                    const email = (user as any).email || user.emails?.[0]?.value || user.userName || '';
                    const name = typeof user.name === 'string' ? user.name : (user.name?.formatted || '');
                    const display = name ? `${name} (${email})` : email;
                    return (
                      <option key={email} value={email}>
                        {display}
                      </option>
                    );
                  })}
                </select>

                {/* Mobile bottom-sheet picker */}
                <div className="md:hidden w-full">
                  <DropdownMenu
                    className="w-full"
                    align="left"
                    trigger={
                      <button
                        type="button"
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] flex items-center justify-between"
                      >
                        <span className="min-w-0 truncate">{selectedUserLabel}</span>
                        <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      </button>
                    }
                    items={[
                      {
                        label: 'Select a user...',
                        icon: !selectedUserId ? <Check className="w-4 h-4" /> : undefined,
                        onClick: () => setSelectedUserId(''),
                      },
                      ...availableUsers.map((user: any) => {
                        const email = user.email || user.emails?.[0]?.value || user.userName || '';
                        const name = typeof user.name === 'string' ? user.name : (user.name?.formatted || '');
                        const display = name ? `${name} (${email})` : email;
                        return {
                          label: display,
                          icon: email === selectedUserId ? <Check className="w-4 h-4" /> : undefined,
                          onClick: () => setSelectedUserId(email),
                          className: 'font-mono',
                        };
                      }),
                    ]}
                  />
                </div>
                <Button
                  onClick={handleAddUser}
                  disabled={!selectedUserId || isSubmitting}
                  className="w-full sm:w-auto"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Members ({members.length})
              </h3>
              {members.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                  No members in this group yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {members.map(member => (
                    <div
                      key={member.value}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md min-h-[44px]"
                    >
                      <span className="text-sm text-gray-900 dark:text-white break-words flex-1 pr-2">
                        {member.display || member.value}
                      </span>
                      {confirmingRemove === member.value ? (
                        <InlineConfirmation
                          onConfirm={() => handleRemoveUser(member.value)}
                          onCancel={() => setConfirmingRemove(null)}
                        />
                      ) : (
                        <button
                          onClick={() => setConfirmingRemove(member.value)}
                          disabled={isSubmitting}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 flex-shrink-0 p-2"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

